// File: TipCardComponent.js
import React, { useState } from 'react';
import { Avatar, Form, Switch, Tooltip, Radio, Row, Col, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  // For "button"/"highlight" styling:
  SimpleProductCard,
  // For tip dropdown styling:
  PreviewCard,
  PreviewImage,
  PreviewContent,
  TipButtonsGrid,
  TipButton,
  CustomAmountInput,
  ProductCard,
  SlideTransition,
  SpotlightButton,
} from './styled';
import tipProducts from '../../../../../../../services/tip/service';

/**
 * Helper for numeric price display
 * (if you want a price somewhere in your fallback)
 */
const displayPrice = (price) => {
  if (price === undefined || price === null) return null;
  return price === 0 ? 'Free' : `$${Number(price).toFixed(2)}`;
};

/**
 * TipCardComponent:
 * Renders a product card differently for `cardType` = "button" or "highlight",
 * and then shows a tip dropdown below the card (the same preview logic).
 */
const TipCardComponent = ({ item, theme = {}, storeName }) => {
  const [showTipCard, setShowTipCard] = useState(false);
  const [selectedTip, setSelectedTip] = useState(String(item.tipOption2Amount || '10'));
  const [isCustomTipEnabled, setIsCustomTipEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleTipSelectChange = (e) => {
    const value = e.target.value;
    setSelectedTip(value);
    setIsCustomTipEnabled(value === 'custom');
  };

  const handleSendTip = async (tipData) => {
    setIsLoading(true);
    try {
      // 1) Call backend to create the tip session
      const { url } = await tipProducts.sendTip(tipData);

      // 2) Redirect the user to Stripe’s checkout page
      window.location.href = url;
    } catch (error) {
      console.error('Error sending tip:', error);
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // 1) Render the main card based on cardType
  // ─────────────────────────────────────────────────────────

  let mainCard = null;

  if (item.cardType === 'button') {
    /**
     * "Button" card type:
     * Remove the small button and just make the entire card clickable.
     * Clicking opens the tip card, so no external href here.
     */
    mainCard = (
      <SimpleProductCard
        key={item.id}
        theme={theme}
        onClick={() => setShowTipCard((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        {/* Only show image if thumbnail is present */}
        {item.thumbnail && (
          <span className="block__image--pill">
            <img
              src={item.thumbnail}
              alt="block image"
              className="base-preview-image"
            />
          </span>
        )}
        {/* Center text. If no thumbnail, just show text in the middle */}
        <div
          className={`block__content ${!item.thumbnail ? 'centered' : ''}`}
          style={{ width: '100%' }}
        >
          <div className="block__text--pill">
            {item.text || item.title || 'Button'}
          </div>
          {/* If there's a price, show it */}
          {item.price !== undefined && (
            <div className="price-review">
              <div className="product-price">
                <span className="amount">{displayPrice(item.price)}</span>
              </div>
            </div>
          )}
        </div>
        {/* If we had a thumbnail, show a spacer to preserve layout */}
        {item.thumbnail && <span className="block__spacer"></span>}
      </SimpleProductCard>
    );
  }

  else if (item.cardType === 'spotlight') {
    mainCard = (
      <ProductCard key={item.id} theme={theme} style={{ cursor: 'pointer' }}>
        {/* Single Row with 2 columns */}
        <Row gutter={[16, 16]} align="stretch" style={{ height: '100%' }}>
          {/* LEFT Column: Image */}
          <Col xs={10} md={8}>
            <Avatar
              shape="square"
              style={{ width: '100%', height: 'auto' }}
              src={item.thumbnail || 'https://via.placeholder.com/150'}
            />
          </Col>

          {/* RIGHT Column: Text & Button */}
          <Col
            xs={14}
            md={16}
            style={{
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Title, subtitle, price */}
            <div>
              <h3 style={{ margin: 0, color: theme?.card_text_color }}>
                {item.text || item.title || 'Unnamed Product'}
              </h3>
              {item.subtitle && (
                <p style={{ margin: 0, color: theme?.card_text_color }}>
                  {item.subtitle}
                </p>
              )}
              {item.price !== undefined && (
                <div className="product-price" style={{ marginTop: '8px' }}>
                  <span className="amount">{displayPrice(item.price)}</span>
                </div>
              )}
            </div>

            {/* The button at the bottom */}
            <SpotlightButton
              theme={theme}
              isTipOpen={showTipCard}
              onClick={() => setShowTipCard((prev) => !prev)}
            >
              <span
                className="button-text"
                style={{ color: theme?.button_text_color }}
              >
                {item.buttonName || 'Send Tip'}
              </span>
              {/* Down arrow if hidden; up arrow if visible */}
              <span style={{ fontSize: '14px' }}>
                {showTipCard ? '▲' : '▼'}
              </span>
            </SpotlightButton>
          </Col>
        </Row>
      </ProductCard>
    );
  }

  // ─────────────────────────────────────────────────────────
  // 2) Return the main card  the TIP dropdown
  //    (No separate tip button; the card itself toggles it)
  // ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Render the main card (button / highlight / fallback) */}
      {mainCard}

      {/* Show the Tip Preview block if toggled on */}
      <SlideTransition isVisible={showTipCard} style={{ marginTop: '10px' }}>
        <PreviewCard theme={theme}>
          {/* 1) Optional checkout image */}
          {item.checkoutImage && (
            <PreviewImage>
              <img src={item.checkoutImage} alt="Tip preview" />
            </PreviewImage>
          )}

          <PreviewContent>
            {/* 2) Title, then description */}
            <h3
              style={{
                fontSize: '24px',
                color: theme?.card_text_color || '#fff',
                marginBottom: '0.5rem',
              }}
            >
              {item.descriptionTitle || 'Support My Work'}
            </h3>
            {item.descriptionBody && (
              <div
                style={{
                  fontSize: '16px',
                  marginBottom: '1rem',
                  color: theme?.card_text_color || '#fff',
                }}
                dangerouslySetInnerHTML={{
                  __html: item.descriptionBody,
                }}
              />
            )}

            {/* 3) Radio.Group to handle tip amounts */}
            <Radio.Group
              onChange={handleTipSelectChange}
              value={selectedTip}
              style={{ width: '100%' }} // so it spans entire width
            >
              <TipButtonsGrid>
                <TipButton
                  as={Radio.Button}
                  value={String(item.tipOption1Amount || '5')}
                  theme={{
                    button_color: theme.card_text_color,
                    button_text_color: theme.button_text_color,
                    button_style: theme.button_style,
                  }}
                >
                  <div className="amount">${item.tipOption1Amount || '5'}</div>
                  {item.tipOption1Label && (
                    <div className="label">{item.tipOption1Label}</div>
                  )}
                </TipButton>

                <TipButton
                  as={Radio.Button}
                  value={String(item.tipOption2Amount || '10')}
                  theme={{
                    button_color: theme.card_text_color,
                    button_text_color: theme.button_text_color,
                    button_style: theme.button_style,
                  }}
                >
                  <div className="amount">${item.tipOption2Amount || '10'}</div>
                  {item.tipOption2Label && (
                    <div className="label">{item.tipOption2Label}</div>
                  )}
                </TipButton>

                <TipButton
                  as={Radio.Button}
                  value={String(item.tipOption3Amount || '25')}
                  theme={{
                    button_color: theme.card_text_color,
                    button_text_color: theme.button_text_color,
                    button_style: theme.button_style,
                  }}
                >
                  <div className="amount">${item.tipOption3Amount || '25'}</div>
                  {item.tipOption3Label && (
                    <div className="label">{item.tipOption3Label}</div>
                  )}
                </TipButton>
              </TipButtonsGrid>
            </Radio.Group>

            {/* 4) Custom tip toggle */}
            {item.allowCustomAmount && (
              <div style={{ marginTop: '1rem' }}>
                <Form.Item>
                  <Switch
                    checked={isCustomTipEnabled}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedTip('custom');
                        setIsCustomTipEnabled(true);
                      } else {
                        setSelectedTip(String(item.tipOption2Amount || '10'));
                        setIsCustomTipEnabled(false);
                      }
                    }}
                  />
                  <span
                    style={{
                      marginLeft: '8px',
                      color: theme?.card_text_color || '#fff',
                    }}
                  >
                    Enter Custom Tip
                    <Tooltip title="Type any tip amount you want">
                      <QuestionCircleOutlined
                        style={{ color: '#9CA3AF', marginLeft: 8 }}
                      />
                    </Tooltip>
                  </span>
                </Form.Item>
              </div>
            )}

            {/* 5) If custom is selected, show input */}
            {item.allowCustomAmount && selectedTip === 'custom' && (
              <>
                <p
                  style={{
                    marginTop: '1rem',
                    marginBottom: '0.25rem',
                    fontWeight: 'bold',
                    color: theme?.card_text_color || '#fff',
                  }}
                >
                  Enter Custom Tip
                </p>
                <CustomAmountInput
                  name="customAmount"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a custom tip amount',
                    },
                    {
                      validator: async (_, value) => {
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                          throw new Error('Please enter a valid number');
                        }
                        if (numValue < 5 || numValue > 1000) {
                          throw new Error('Tip must be between $5 and $1000');
                        }
                      },
                    },
                  ]}
                >
                  <input
                    type="number"
                    placeholder="Enter custom tip amount"
                    min="5"
                    max="1000"
                    step="5"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />
                </CustomAmountInput>
              </>
            )}

            {/* 6) Submit button */}
            <button
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '20px',
                backgroundColor: theme?.button_color || '#2ecc71',
                border: 'none',
                borderRadius: '8px',
                color: theme?.button_text_color || '#fff',
                fontSize: '16px',
                cursor: 'pointer',
              }}
              disabled={isLoading}
              onClick={() =>
                handleSendTip({
                  amount: Number(selectedTip),
                  title: item?.descriptionTitle || '',
                  description: item?.descriptionBody || '',
                  storeName: storeName,
                })
              }
            >
              {isLoading ? (
                // 5) Show a loading spinner from Ant Design (or a custom spinner)
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Spin size="small" />
                  {/* optional text next to spinner */}
                  <span style={{ marginLeft: '8px' }}>Processing...</span>
                </div>
              ) : (
                'Send Tip'
              )}
            </button>
          </PreviewContent>
        </PreviewCard>
      </SlideTransition>
    </div>
  );
};

export default TipCardComponent;
